import { DurableAudioSessionManager } from "../DurableAudioSessionManager";

interface WebSocketLike {
  readyState: number;
  send(data: ArrayBuffer): void;
}

export class ReplayOrchestrator {
  private active = false;

  constructor(
    private readonly manager: DurableAudioSessionManager,
    private readonly options: {
      isSocketOpen: () => boolean;
      getSocket: () => WebSocketLike | null;
      waitForTransportDrain: () => Promise<boolean>;
      onReplayProgress?: (bytesSent: number) => void;
    }
  ) {}

  isActive(): boolean {
    return this.active;
  }

  async replayFromCommittedBoundary(): Promise<{ bytesSent: number; segmentsSent: number }> {
    if (this.active) {
      return { bytesSent: 0, segmentsSent: 0 };
    }

    this.active = true;
    let bytesSent = 0;
    let segmentsSent = 0;

    try {
      const replayPlan = await this.manager.prepareReplayPlan();

      if (!replayPlan) {
        await this.manager.completeReplayCycle();
        return { bytesSent, segmentsSent };
      }

      let nextSeq = replayPlan.replayFromSeq;

      while (true) {
        if (!this.options.isSocketOpen()) {
          break;
        }

        const batch = await this.manager.getReplayBatch(nextSeq);

        if (batch.done || batch.payloads.length === 0) {
          await this.manager.completeReplayCycle();
          break;
        }

        const socket = this.options.getSocket();

        if (!socket) {
          break;
        }

        for (let index = 0; index < batch.payloads.length; index += 1) {
          const payload = batch.payloads[index];
          socket.send(payload.payload);
          bytesSent += payload.payload.byteLength;
          segmentsSent += 1;

          const drained = await this.options.waitForTransportDrain();

          if (!drained) {
            return { bytesSent, segmentsSent };
          }
        }

        nextSeq = batch.nextSeq;
        if (this.options.onReplayProgress) {
          this.options.onReplayProgress(bytesSent);
        }
      }

      return { bytesSent, segmentsSent };
    } finally {
      this.active = false;
    }
  }
}
